import { logger } from '@adornis/base/logging.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { ColorDefinitions } from '@adornis/buchungsheld-db/entities/ColorDefinitions.js';
import { Institution } from '@adornis/buchungsheld-db/entities/institution.js';
import { Setting, SettingColorDefintionProp } from '@adornis/buchungsheld-db/entities/settings.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { property, state } from 'lit/decorators.js';
import { of, switchMap, tap } from 'rxjs';
import { BHFormController } from '../../../../controller/BHFormController.js';

export class EinrichtungSettingsAdapter extends BuchungsheldLitElement {
  @property({ attribute: 'institution-id' }) institutionID = new RXController<string | null>(this, null);

  @state() protected readonly _institution = new RXController(this, this.institutionID.observable, obs =>
    obs.pipe(
      switchMap(institutionID =>
        institutionID
          ? Institution.subscribeByID<Institution>(institutionID)({
              ...Institution.allFields,
              settings: {
                ...selectionSet(() => Setting, 1),
                colorDefinitions: selectionSet(() => ColorDefinitions, 3),
                inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
              },
            })
          : of(null),
      ),
      tap(institution => {
        console.log('INSTITUTION?: ', institution);
        if (!institution) return;
        if (!institution?.settings) throw new Error('settings are not defined');
        if (!institution.settings.colorDefinitions) institution.settings.colorDefinitions = new ColorDefinitions({});
      }),
    ),
  );

  @state() protected readonly _form = new BHFormController(this, this._institution.observable, {
    success: async institution => {
      try {
        await institution.save();
        if (institution.settings._id) await institution.settings.save();
        else await institution.settings.create();
        await XSnackbar.show(this._translation.translate('save'));
      } catch (e) {
        logger.error({ err: e }, 'Es gab ein Problem beim Speichern der Institution');
        if (e instanceof ValidationError)
          await XDialog.alert(
            `${this._translation.translate('error_saving')}: ${this._translation.translate(
              'err',
              e.extensions.translationKey,
            )}`,
          );
        else await XDialog.alert(this._translation.translate('error_saving') + ' ' + (e as Error).message);
      }
    },
    error: err => {
      logger.error({ err }, 'Es gab ein Problem beim Speichern der Institution');
      const message = (() => {
        if (err.extensions.translationKey === 'validation_non_optional') {
          return this._translation.translate('validation_required_fields_missing');
        }

        if (err.message.includes('ValidationError') || err instanceof ValidationError) {
          return this._translation.translate('check_form');
        }

        return (err as any).message;
      })();

      return XSnackbar.show(message);
    },
  });
}
